import { useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useConfirmationModal, useModals, useSnackbars } from '@customerio/pluma-components/react';
import { useAnalytics } from '@/services/analytics';
import { getApiClientErrorDetailFromError, getApiClientErrorStatus } from '@/services/api/errors';
import { appKeys, type App, type AppUsage } from '@/services/db/app/entity';
import { getAppUsage } from '@/services/db/app/queries';
import { deleteApp, updateApp } from '@/services/db/app/mutations';
import { AppDeleteInUseModal } from '../components/app-delete-in-use-modal';

function defaultAppMessage(app: App, displaced: App | undefined): string {
  if (displaced == null) {
    return `New push messages will start with "${app.name}" selected.`;
  }
  return `"${displaced.name}" will no longer be the default. New push messages will start with "${app.name}" selected instead.`;
}

type DeletePrompt = { kind: 'confirmation' } | { kind: 'in-use'; usage: AppUsage };

const PLAIN_DELETE: DeletePrompt = { kind: 'confirmation' };

const HTTP_BAD_REQUEST = 400;

// The server refuses an in-use app as "unable to delete app: used by 2 push messages".
const IN_USE_DETAIL = /^unable to delete app: (used by .+)$/;

function inUseRefusalMessage(app: App, error: unknown): string {
  const reason = IN_USE_DETAIL.exec(getApiClientErrorDetailFromError(error) ?? '')?.[1];
  return reason == null
    ? `"${app.name}" couldn't be deleted because messages still use it.`
    : `"${app.name}" couldn't be deleted because it's ${reason}. Remove it from these messages first.`;
}

export type UseAppActionsArgs = {
  workspaceId: number;
  apps: readonly App[];
  onDeleted?: () => void;
};

/** Unlike the form, these are persisted the moment they are confirmed; nothing here is staged. */
export function useAppActions({ workspaceId, apps, onDeleted }: UseAppActionsArgs) {
  const qc = useQueryClient();
  const confirm = useConfirmationModal();
  const modals = useModals();
  const snackbars = useSnackbars();
  const analytics = useAnalytics();

  const [isDeleting, setIsDeleting] = useState(false);
  // Read synchronously: a second click lands long before the state that
  // disables the control has re-rendered.
  const isDeletingRef = useRef(false);

  const reloadApps = async (appId: number) => {
    await qc.invalidateQueries({ queryKey: appKeys.list(workspaceId) });
    await qc.invalidateQueries({ queryKey: appKeys.detail(workspaceId, appId) });
  };

  const requestSetDefault = async (app: App) => {
    const displaced = apps.find((entry) => entry.is_default && entry.id !== app.id);
    await confirm({
      title: `Make "${app.name}" the default app?`,
      message: defaultAppMessage(app, displaced),
      confirmLabel: 'Set as default',
      cancelLabel: 'Cancel',
      onConfirm: async () => {
        try {
          await updateApp({ workspaceId, appId: app.id, is_default: true });
          // A workspace holds exactly one default, so the server demotes the
          // previous holder — re-read the list rather than move one label.
          await reloadApps(app.id);
        } catch {
          snackbars.create({
            variant: 'critical',
            id: 'app_default.error',
            message: `"${app.name}" couldn't be made the default. Check your connection and try again.`,
          });
        }
      },
    });
  };

  const resolveDeletePrompt = async (app: App): Promise<DeletePrompt> => {
    try {
      const usage = await getAppUsage(workspaceId, app.id);
      return usage.in_use ? { kind: 'in-use', usage } : PLAIN_DELETE;
    } catch {
      // Safe to fall back: DELETE refuses an in-use app with a 400
      // (services `ui_api/apps/delete.go`), and runDelete explains the refusal.
      return PLAIN_DELETE;
    }
  };

  const showInUse = (app: App, usage: AppUsage) => {
    void analytics.track('push', 'appDeletionWarningViewed', {
      app_id: String(app.id),
      affected_message_count: usage.total_count,
    });
    modals.open(AppDeleteInUseModal, { appName: app.name, usage });
  };

  const explainRefusedDelete = async (app: App, error: unknown) => {
    const prompt = await resolveDeletePrompt(app);
    if (prompt.kind === 'in-use') {
      showInUse(app, prompt.usage);
      return;
    }
    snackbars.create({ variant: 'critical', id: 'app_delete.error', message: inUseRefusalMessage(app, error) });
  };

  const runDelete = async (app: App) => {
    try {
      await deleteApp({ workspaceId, appId: app.id });
      await qc.invalidateQueries({ queryKey: appKeys.list(workspaceId) });
      qc.removeQueries({ queryKey: appKeys.detail(workspaceId, app.id) });
      qc.removeQueries({ queryKey: appKeys.usage(workspaceId, app.id) });
      onDeleted?.();
    } catch (error) {
      if (getApiClientErrorStatus(error) === HTTP_BAD_REQUEST) {
        await explainRefusedDelete(app, error);
        return;
      }
      snackbars.create({
        variant: 'critical',
        id: 'app_delete.error',
        message: `"${app.name}" couldn't be deleted. Check your connection and try again.`,
      });
    }
  };

  const presentDeletePrompt = async (app: App, prompt: DeletePrompt) => {
    if (prompt.kind === 'in-use') {
      showInUse(app, prompt.usage);
      return;
    }
    const confirmed = await confirm({
      title: `Delete ${app.name}?`,
      message: 'This app and its credentials will be permanently deleted.',
      confirmLabel: 'Delete app',
      cancelLabel: 'Cancel',
      isCritical: true,
    });
    if (confirmed) {
      await runDelete(app);
    }
  };

  const requestDelete = async (app: App) => {
    if (isDeletingRef.current) {
      return;
    }
    isDeletingRef.current = true;
    setIsDeleting(true);
    try {
      await presentDeletePrompt(app, await resolveDeletePrompt(app));
    } finally {
      isDeletingRef.current = false;
      setIsDeleting(false);
    }
  };

  return { isDeleting, requestSetDefault, requestDelete };
}
